import { useRecoilValue } from 'recoil'
import { userPrefsAtom, userUserRoleAtom } from 'atoms'
import { useMemo, useCallback } from 'react'
import useInnovaAxios from './useInnovaAxios'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import { useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from '../queryKeys'

export const UNITS_FOR = {
  Depth: 'depth',
  Dogleg: 'dogleg',
  Diameter: 'diameter',
  MudWeight: 'mud',
  FlowRate: 'flow',
  Pressure: 'pressure',
  Temperature: 'temp',
  Torque: 'torque',
  Weight: 'weight',
  Cost: 'cost',
  Volume: 'volume',
  Area: 'area',
  UnitWeight: 'unitWeight',
  LatLong: 'latLong',
  Rop: 'rop',
  BendingMoment: 'bendingMoment',
  Force: 'force',
  Power: 'power',
  Energy: 'energy',
  Time: 'time',
  Degree: 'degree', 
  Percentage: 'percentage',
}

const EMPTY_DROPDOWNS = {
  depth: [],
  diameter: [],
  dogleg: [],
  flow: [],
  pressure: [],
  volume: [],
  mud: [],
  weight: [],
  torque: [],
  unitWeight: [],
  temp: [],
  cost: [],
  area: [],
  latLong: [],
  bendingMoment: [],
  force: [],
  power: [],
  energy: [],
  time: [],
  degree: [],
  percentage: [],
}

const useUnits = () => {
  const userRole = useRecoilValue(userUserRoleAtom)
  const userPrefs = useRecoilValue(userPrefsAtom)
  const { databaseOrg } = useInnovaAuth()

  const getUnitSetMap = useInnovaAxios({ url: '/units/getUnitSetMap' })
  const getUnitSetList = useInnovaAxios({ url: '/units/getUnitSetList' })

  const { data: unitMapRaw, refetch: refetchUnitSetMap } = useQuery({
    queryKey: [QUERY_KEYS.unitSetMapKey],
    queryFn: async () => {
      const res = await getUnitSetMap()
      if (!res?.data) throw new Error('Failed to fetch unitSetMap')
      return res.data
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 3,
  })

  const { data: unitListRaw, refetch: refetchUnitSetList } = useQuery({
    queryKey: [QUERY_KEYS.unitSetListKey, databaseOrg],
    queryFn: async () => {
      const res = await getUnitSetList()
      if (!res?.data) throw new Error('Failed to fetch unitSetList')
      return res.data
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 3,
    enabled: !!databaseOrg,
  })

  const unitMap = unitMapRaw || null

  const dropDowns = useMemo(() => {
    if (!unitMap) return EMPTY_DROPDOWNS
    const result = {}
    for (const property in unitMap) {
      const arr = Array.isArray(unitMap[property]) ? unitMap[property] : []
      result[property] = arr.map((val) => ({ label: val.Name, value: val.Name }))
    }
    return { ...EMPTY_DROPDOWNS, ...result }
  }, [unitMap])

  const { unitList, unitListOrg } = useMemo(() => {
    if (!Array.isArray(unitListRaw)) return { unitList: [], unitListOrg: [] }

    const all = []
    const orgOnly = []

    unitListRaw.forEach((val, index) => {
      const item = { label: val.name, value: index, organization: val.organization }
      all.push(item)
      if (val.organization === userRole.organization) {
        orgOnly.push(item)
      }
    })

    return { unitList: all, unitListOrg: orgOnly }
  }, [unitListRaw, userRole.organization])

  const getUnitSelectionForUser = useCallback(
    (unit) => {
      if (!userPrefs?.unitset) return null
      return userPrefs.unitset[unit] ?? null
    },
    [userPrefs],
  )

  const getUnitConversion = useCallback(
    (unit, dbToUser = true) => {
      if (!unitMapRaw?.hasOwnProperty(unit)) return 1
      if (!Array.isArray(unitMapRaw[unit])) return 1

      const unitSelection = getUnitSelectionForUser(unit)
      if (!unitSelection) return 1

      const record = unitMapRaw[unit].find((u) => u.Name === unitSelection)
      if (!record) return 1

      return dbToUser ? record.conversion : 1 / record.conversion
    },
    [unitMapRaw, getUnitSelectionForUser],
  )

  const getDoglegBase = useCallback(() => {
    if (!unitMapRaw?.[UNITS_FOR.Dogleg]) return 100
    const unitSelection = getUnitSelectionForUser(UNITS_FOR.Dogleg)
    const record = unitMapRaw[UNITS_FOR.Dogleg].find((u) => u.Name === unitSelection)
    const dlBase = record?.text ?? 100
    return parseFloat(dlBase)
  }, [unitMapRaw, getUnitSelectionForUser])

  const getUnitsText = useCallback(
    (type) => {
      let defaultUnit = type === UNITS_FOR.Cost ? '' : '-'

       if (type === UNITS_FOR.Degree) {
        return 'Deg'
      }

      if (type === UNITS_FOR.Percentage) {
        return '%'
      }

      if (!userPrefs || !unitMapRaw || !type || (type !== UNITS_FOR.Rop && !userPrefs.unitset?.hasOwnProperty(type))) return defaultUnit

      let isRop = false
      if (type === UNITS_FOR.Rop) {
        isRop = true
        type = UNITS_FOR.Depth
      }

      const unitSelection = userPrefs.unitset[type]
      const arr = unitMapRaw[type]
      if (!Array.isArray(arr)) return defaultUnit

      let unitText = defaultUnit
      arr.forEach((unit) => {
        if (unit.Name === unitSelection) {
          unitText = unit.text
        }
      })

      if (type === UNITS_FOR.Dogleg) {
        return `°/${unitText} ${getUnitsText(UNITS_FOR.Depth)}`
      }

      if (isRop) {
        return unitText + '/hr'
      }

      return unitText
    },
    [userPrefs, unitMapRaw],
  )

  const refreshUnits = async () => {
    await Promise.all([refetchUnitSetMap(), refetchUnitSetList()])
  }

  return { getUnitsText, dropDowns, unitList, unitListOrg, refreshUnits, getUnitConversion, getDoglegBase }
}

export default useUnits
